import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";
import { formatPrice } from "@/utils/formatPrice";
import { useTranslation } from "react-i18next";

type WizardStep = "type" | "details" | "payment" | "confirmation";

interface WizardFooterProps {
  // Generic step (Alt B refactor 2026-05-27): was literal WizardStep,
  // now string|number so non-booking consumers (ActivityWizard) can use.
  step: WizardStep | string | number;
  onAction: () => void;
  actionLabel: string;
  isLoading?: boolean;
  disabled?: boolean;
  // Booking-specific price-row (Alt B refactor): now optional. When undefined,
  // the price-row is not rendered (activity-wizard placeholder steps have no price).
  playerCost?: number | null;
  totalCost?: number;
  // Hide-on-steps (Alt B refactor + operator-justering): replaces the hard-coded
  // `if (step === "type" || step === "confirmation") return null` early-return.
  // Default for BookingWizard preserves existing behavior; ActivityWizard supplies
  // its own list. Pass empty array to render on all steps.
  hideOnSteps?: Array<string | number>;
  // Custom price-line override (Alt B refactor): when set, replaces the
  // default booking price-row (youPay + totalEntireBooking lines).
  customPriceLine?: ReactNode;
  // i18n-namespace (Alt B refactor): default "booking"; activity passes "admin".
  i18nNamespace?: string;
}

const DEFAULT_BOOKING_HIDE_STEPS: Array<string | number> = ["type", "confirmation"];

export default function WizardFooter({
  step,
  playerCost,
  totalCost,
  actionLabel,
  onAction,
  isLoading = false,
  disabled = false,
  hideOnSteps,
  customPriceLine,
  i18nNamespace,
}: WizardFooterProps) {
  const { t } = useTranslation(i18nNamespace ?? "booking");

  // Early-return: default-list preserves BookingWizard's existing behavior
  // (hide on "type" + "confirmation"). Activity-wizard passes its own list
  // (e.g. [] to render on all steps, or [1, 6] to hide first/last).
  const hideList = hideOnSteps ?? DEFAULT_BOOKING_HIDE_STEPS;
  if (hideList.includes(step)) return null;

  // Price-row rendering: customPriceLine wins; falls back to booking price-row;
  // suppressed entirely if neither customPriceLine nor playerCost provided.
  const hasBookingPriceRow = playerCost !== undefined;
  const priceLoading = playerCost === null;
  const totalForCompare = totalCost ?? 0;

  return (
    <div className="sticky bottom-0 border-t bg-background px-4 py-3 space-y-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
      {customPriceLine ? (
        customPriceLine
      ) : hasBookingPriceRow ? (
        <>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">{t("youPay")}</span>
            {priceLoading ? (
              <span className="inline-block w-20 h-7 bg-muted animate-pulse rounded" />
            ) : (
              <span className="text-xl font-bold">{formatPrice(playerCost)}</span>
            )}
          </div>

          {!priceLoading && playerCost != null && totalForCompare !== playerCost && totalForCompare > 0 && (
            <div className="flex justify-between items-center text-xs text-muted-foreground">
              <span>{t("totalEntireBooking")}</span>
              <span>{formatPrice(totalForCompare)}</span>
            </div>
          )}
        </>
      ) : null}

      <Button
        className="w-full livfull-btn-gradient text-white"
        size="lg"
        onClick={onAction}
        disabled={isLoading || disabled || (hasBookingPriceRow && priceLoading)}
      >
        {isLoading ? (
          <Loader2 className="animate-spin h-4 w-4 mr-2" />
        ) : null}
        {actionLabel}
      </Button>
    </div>
  );
}
